import React, { useState, useRef, useEffect } from 'react';
import {
  TextField,
  Popover,
  Box,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CalendarToday } from '@mui/icons-material';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parse, isValid } from 'date-fns';

const DateInput = ({
  value,
  onChange,
  label,
  placeholder = "DD/MM/YYYY",
  size = "small",
  sx = {},
  ...props
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [textValue, setTextValue] = useState('');
  const [dateValue, setDateValue] = useState(null);
  const inputRef = useRef(null);

  // Initialize values
  useEffect(() => {
    if (value) {
      // If value is a date string, try to parse it
      if (typeof value === 'string') {
        const parsedDate = parse(value, 'dd/MM/yyyy', new Date());
        if (isValid(parsedDate)) {
          setDateValue(parsedDate);
          setTextValue(value);
        } else {
          setTextValue(value);
        }
      } else if (value instanceof Date) {
        setDateValue(value);
        setTextValue(format(value, 'dd/MM/yyyy'));
      }
    } else {
      setTextValue('');
      setDateValue(null);
    }
  }, [value]);

  const handleTextChange = (e) => {
    const newTextValue = e.target.value;
    setTextValue(newTextValue);
    
    // Try to parse the text as date
    if (newTextValue.length === 10) {
      const parsedDate = parse(newTextValue, 'dd/MM/yyyy', new Date());
      if (isValid(parsedDate)) {
        setDateValue(parsedDate);
        onChange?.(newTextValue);
      } else {
        setDateValue(null);
        onChange?.(newTextValue);
      }
    } else {
      setDateValue(null);
      onChange?.(newTextValue);
    }
  };

  const handleDatePickerChange = (newDate) => {
    if (newDate && isValid(newDate)) {
      const formattedDate = format(newDate, 'dd/MM/yyyy');
      setDateValue(newDate);
      setTextValue(formattedDate);
      onChange?.(formattedDate);
    }
    setAnchorEl(null);
  };

  const handleCalendarClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAnchorEl(inputRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <TextField
        ref={inputRef}
        value={textValue}
        onChange={handleTextChange}
        label={label}
        placeholder={placeholder}
        size={size}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={handleCalendarClick}
                edge="end"
                size="small"
                sx={{
                  color: 'text.secondary',
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.04)',
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.12)'
                      : 'rgba(0, 0, 0, 0.08)'
                  }
                }}
                onMouseDown={(e) => e.preventDefault()}
              >
                <CalendarToday fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiInputBase-root': {
            height: '40px',
            fontSize: '0.875rem',
            borderRadius: 2,
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.875rem',
          },
          ...sx,
        }}
        {...props}
      />
      
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            boxShadow: (theme) => theme.palette.mode === 'dark'
              ? '0px 10px 30px rgba(0, 0, 0, 0.5)'
              : '0px 10px 30px rgba(0, 0, 0, 0.1)',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: (theme) => theme.palette.mode === 'dark'
              ? 'rgba(30, 30, 30, 0.85)'
              : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(12px)',
          }
        }}
      >
        <Box sx={{ p: 1 }}>
          <StaticDatePicker
            value={dateValue}
            onChange={handleDatePickerChange}
            displayStaticWrapperAs="desktop"
            slotProps={{
              day: {
                sx: {
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  },
                },
              },
              toolbar: {
                sx: {
                  '& .MuiPickersToolbar-content': {
                    display: 'none',
                  },
                },
              },
              actionBar: {
                sx: {
                  display: 'none',
                },
              },
            }}
          />
        </Box>
      </Popover>
    </LocalizationProvider>
  );
};

export default DateInput;
